import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { APP_CONFIG } from '@environments/environment';
import { withSerializedDates } from '@/app/shared/utils';

export interface DeviationsResponseDto {
    id: string;
    productId: string;
    deviationNumber: string;
    description: string;
    referenceNote: string;
    dateOpened: Date;
    dateClosed: Date;
    status: string;
    impact: string;
    resolution: string;
    source: string;
    shipmentId: string | null;
    lotNumber: string | null;
    correctiveAction: string | null;
    preventiveAction: string | null;
    sourceDocumentId: string | null;
    product?: {
        id: string;
        productName: string;
        productItemCode?: string;
    };
    createdAt: Date;
    updatedAt: Date;
}

export interface CreateDeviationsRequestDto {
    productId: string;
    deviationNumber?: string;
    description?: string;
    referenceNote?: string;
    dateOpened?: Date;
    dateClosed?: Date;
    status?: string;
    impact?: string;
    resolution?: string;
    source?: string;
    lotNumber?: string;
    correctiveAction?: string;
    preventiveAction?: string;
}

export interface UpdateDeviationsRequestDto {
    deviationNumber?: string;
    description?: string;
    referenceNote?: string;
    dateOpened?: Date;
    dateClosed?: Date;
    status?: string;
    impact?: string;
    resolution?: string;
    source?: string;
    lotNumber?: string;
    correctiveAction?: string;
    preventiveAction?: string;
}

export interface DeviationStatsDto {
    total: number;
    open: number;
    inProgress: number;
    closed: number;
    timeline: Array<{ month: string; opened: number; closed: number }>;
}

export interface DeviationsQueryParams {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    source?: string;
    productId?: string;
    dateFrom?: string;
    dateTo?: string;
}

export interface PaginatedDeviations {
    items: DeviationsResponseDto[];
    meta: {
        totalCount: number;
        totalPages: number;
        currentPage: number;
        limit: number;
    };
}

@Injectable({
    providedIn: 'root',
})
export class DeviationsService {
    private http = inject(HttpClient);
    private apiUrl = `${APP_CONFIG.apiUrl}/products`;
    private deviationsUrl = `${APP_CONFIG.apiUrl}/deviations`;

    getByProductId(productId: string): Observable<DeviationsResponseDto[]> {
        return this.http
            .get<any>(`${this.apiUrl}/${productId}/deviations`)
            .pipe(map((response) => response.data || response));
    }

    create(data: CreateDeviationsRequestDto): Observable<DeviationsResponseDto> {
        return this.http
            .post<any>(`${this.apiUrl}/${data.productId}/deviations`, withSerializedDates(data, ['dateOpened', 'dateClosed']))
            .pipe(map((response) => response.data || response));
    }

    update(productId: string, id: string, data: UpdateDeviationsRequestDto): Observable<DeviationsResponseDto> {
        return this.http
            .patch<any>(`${this.apiUrl}/${productId}/deviations/${id}`, withSerializedDates(data, ['dateOpened', 'dateClosed']))
            .pipe(map((response) => response.data || response));
    }

    delete(productId: string, id: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${productId}/deviations/${id}`);
    }

    getAll(query?: DeviationsQueryParams): Observable<PaginatedDeviations> {
        let params = new HttpParams();
        if (query) {
            if (query.page) params = params.set('page', query.page.toString());
            if (query.limit) params = params.set('limit', query.limit.toString());
            if (query.search) params = params.set('search', query.search);
            if (query.status) params = params.set('status', query.status);
            if (query.source) params = params.set('source', query.source);
            if (query.productId) params = params.set('productId', query.productId);
            if (query.dateFrom) params = params.set('dateFrom', query.dateFrom);
            if (query.dateTo) params = params.set('dateTo', query.dateTo);
        }
        return this.http
            .get<any>(this.deviationsUrl, { params })
            .pipe(map((response) => ({
                items: response.data || [],
                meta: response.meta || {},
            })));
    }

    getById(id: string): Observable<DeviationsResponseDto> {
        return this.http
            .get<any>(`${this.deviationsUrl}/${id}`)
            .pipe(map((response) => response.data || response));
    }

    getStats(): Observable<DeviationStatsDto> {
        return this.http
            .get<any>(`${this.deviationsUrl}/stats`)
            .pipe(map((response) => response.data || response));
    }

    exportPdf(id: string): Observable<Blob> {
        return this.http.get(`${this.deviationsUrl}/export/pdf/${id}`, {
            responseType: 'blob',
        });
    }
}
